import * as React from "react"
import { PenLine, Trash2 } from "lucide-react"

import { AddSignatureDialog } from "@/components/shared/signatures"
import { Button } from "@/components/shadcn/ui/button"
import { Label } from "@/components/shadcn/ui/label"
import type { SignatureField as SignatureFieldType } from "@/models/new/forms.types"
import type { ResponseValue } from "@/models/new/formResponse.types"

interface Props {
  field: SignatureFieldType
  value?: ResponseValue
  onChange: (fieldId: string, value: ResponseValue) => void
  isCreating?: boolean
}

const SignatureField = ({ field, value, onChange, isCreating }: Props) => {
  const [dialogOpen, setDialogOpen] = React.useState(false)
  if (!isCreating) return null

  const signatures = value?.type === "signature" ? value.value : []

  return (
    <div className="w-1/2 p-4">
      <Label className="font-medium text-gray-700">
        {field.label}
        {field.required ? <span className="ml-1 text-red-600">*</span> : null}
      </Label>
      {field.description ? (
        <p className="mt-1 text-sm text-slate-500">{field.description}</p>
      ) : null}
      <Button type="button" variant="outline" className="mt-3" onClick={() => setDialogOpen(true)}>
        <PenLine className="h-4 w-4" />
        Add signature
      </Button>

      {signatures.map((signature, index) => (
        <div
          key={`${signature.name}-${signature.value}`}
          className="mt-3 flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3"
        >
          <img
            src={signature.value}
            alt={`Signature of ${signature.name}`}
            className="h-12 w-28 rounded border bg-white object-contain"
          />
          <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{signature.name}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Remove signature for ${signature.name}`}
            onClick={() =>
              onChange(field.id, {
                fieldName: field.name,
                type: "signature",
                value: signatures.filter((_, signatureIndex) => signatureIndex !== index),
              })
            }
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <AddSignatureDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        fieldLabel={field.label}
        onSave={(signature) => {
          onChange(field.id, {
            fieldName: field.name,
            type: "signature",
            value: [...signatures, signature],
          })
          setDialogOpen(false)
        }}
      />
    </div>
  )
}

export default SignatureField
